import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import type { StoreProduct } from '../../../mocks/storeProducts';

interface Props {
  product: StoreProduct;
  onAddToCart: (product: StoreProduct) => void;
}

export const StoreProductCard: React.FC<Props> = ({ product, onAddToCart }) => {
  const { user } = useAuth();
  const [imgError, setImgError] = useState(false);

  const handleBuyNow = () => {
    window.open(product.paymentLink, '_blank', 'noopener,noreferrer');
  };

  const handleAddToCart = () => {
    onAddToCart(product);
  };

  const fallback =
    product.type === 'book'
      ? 'https://public.readdy.ai/ai/img_res/0d65aca68ba9dbb1abf0303808b4c416.jpg'
      : 'https://readdy.ai/api/search-image?query=iOS%20app%20icon%20minimal%20clean%20design%20colorful%20gradient%20rounded%20square%20professional&width=400&height=400&seq=app-fallback&orientation=squarish';

  return (
    <div className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col group relative">

      {/* Product image */}
      <div
        className={`relative overflow-hidden bg-gray-50 ${product.type === 'book' ? 'h-56' : 'h-48 flex items-center justify-center p-6'}`}
      >
        <img
          src={imgError ? fallback : product.image}
          alt={product.title}
          onError={() => setImgError(true)}
          className={`${
            product.type === 'book'
              ? 'w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300'
              : 'w-28 h-28 object-cover rounded-2xl shadow-lg group-hover:scale-105 transition-transform duration-300'
          }`}
        />
        {product.badge && (
          <span className="absolute top-2 left-2 bg-gray-900 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            {product.badge}
          </span>
        )}
        <span
          className={`absolute top-2 right-2 text-xs font-semibold px-2.5 py-1 rounded-full ${
            product.type === 'book' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'
          }`}
        >
          {product.type === 'book' ? 'Book' : 'App'}
        </span>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{product.subCategory}</p>
        <h3 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2 leading-snug">{product.title}</h3>
        <p className="text-xs text-gray-500 mb-4 line-clamp-2 leading-relaxed">{product.description}</p>

        {product.rating !== undefined && (
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map(s => (
              <i
                key={s}
                className={`ri-star-${s <= Math.round(product.rating!) ? 'fill' : 'line'} text-amber-400 text-xs`}
              ></i>
            ))}
            <span className="text-xs text-gray-400 ml-1">({product.reviews})</span>
          </div>
        )}

        <div className="mt-auto">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl font-bold text-gray-900">{product.price}</span>
            {product.price !== 'Free' && (
              <span className="text-xs text-gray-400">One-time purchase</span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleAddToCart}
              className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
            >
              <i className="ri-shopping-cart-line text-sm"></i>
              Add
            </button>
            <button
              onClick={handleBuyNow}
              className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy Now
            </button>
          </div>
          {product.downloadUrl && (
            <Link
              to="/download"
              className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg py-2 hover:bg-green-100 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line text-sm"></i>
              </div>
              PDF Download Available
            </Link>
          )}
          {!user && (
            <p className="text-center text-xs text-gray-400 mt-2">
              <a href="/login" className="underline hover:text-gray-600 transition-colors">Sign in</a> to save order history
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
